// Auth client helpers.
//
// Auth strategy: dual-track. Neue Logins bekommen bereits ein HttpOnly-Cookie
// (`auth_token`) vom Backend gesetzt. Für **gleicher-Origin**-Requests
// (Prod-Domain `hauptstadtstickerei.companylife.de` = FE + BE) fügen wir
// jetzt `credentials: 'include'` hinzu — damit wird der JWT über das
// HttpOnly-Cookie mitgeschickt (XSS-sicher). Für Cross-Origin (Preview-Env
// `*.preview.emergentagent.com` mit `Access-Control-Allow-Origin: *`) bleibt
// der Bearer-Header aus `localStorage` bestehen, weil der Browser bei einem
// Wildcard-CORS keine Credentials mitschickt.
//
// Konsequenz: In Produktion ist der Token nicht mehr aus dem DOM zugreifbar —
// XSS kann ihn nicht klauen. Preview-Env behält den kompatiblen Bearer-Flow.

export const COOKIE_AUTH_MARKER = 'cookie-auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const _isSameOrigin = (url) => {
  try {
    const u = new URL(url, window.location.href);
    return u.origin === window.location.origin;
  } catch {
    return false;
  }
};

const isBackendRequest = (input) => {
  try {
    const url = typeof input === 'string' ? input : input?.url;
    if (!url) return false;
    if (BACKEND_URL && url.startsWith(BACKEND_URL)) return true;
    if (url.startsWith('/api/')) return true;
    return false;
  } catch {
    return false;
  }
};

const clearLocalAuth = () => {
  try {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
  } catch (err) {
    console.warn('[authClient] localStorage cleanup failed', err);
  }
};

const PUBLIC_PATHS = new Set([
  '/', '/login', '/register', '/registrieren', '/subscription',
  '/uebersicht',
  '/privacy', '/datenschutz', '/impressum', '/terms', '/agb',
  '/home-classic', '/forgot-password', '/passwort-vergessen',
  '/reset-password', '/passwort-zuruecksetzen', '/widerruf',
  '/withdrawal', '/widerrufsformular', '/withdrawal-form',
  '/gewaehrleistung', '/gewährleistung', '/warranty',
  '/partner/bewerben', '/affiliate/apply', '/features', '/funktionen',
  '/rechner', '/kennzahlen-rechner',
  '/einladung/annehmen', '/accept-invite', '/accept-invitation',
]);

const handleUnauthorized = () => {
  clearLocalAuth();
  const path = window.location.pathname;
  if (path.startsWith('/admin')) return;
  if (path === '/lms' || path.startsWith('/lms/')) return;
  if (path.startsWith('/kurs/') && path.endsWith('/vorschau')) return;
  if (PUBLIC_PATHS.has(path) || path.startsWith('/play/') || path.startsWith('/partner/') || path.startsWith('/karte/') || path === '/blog' || path.startsWith('/blog/') || path === '/unsere-partner' || path === '/shop' || path.startsWith('/shop/') || path.startsWith('/themenwelten/') || path.startsWith('/design/') || path.startsWith('/kundenkonto') || path === '/checkout' || path.startsWith('/checkout/') || path.startsWith('/merkzettel') || path.startsWith('/review/')) return;
  window.location.href = '/login?expired=true';
};

let installed = false;

// We still install a light fetch wrapper that:
//   - Detects 401 on backend calls and bounces the user to /login
//   - Does NOT add `credentials: 'include'` — the production ingress sets
//     `Access-Control-Allow-Origin: *` on some responses, which the browser
//     blocks when paired with credentialed requests. Bearer header works on
//     any CORS configuration so we sidestep the issue entirely.
export const installAuthFetch = () => {
  if (installed || typeof window === 'undefined' || !window.fetch) return;
  installed = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input, init = {}) => {
    // SEC-303 · Same-Origin-Requests bekommen `credentials: 'include'`, damit
    // das HttpOnly-Cookie als Auth-Träger mitgeschickt wird. Cross-Origin
    // (Preview-Env mit Wildcard-CORS) bleibt bewusst credentialless.
    const url = typeof input === 'string' ? input : input?.url || '';
    if (isBackendRequest(url) && _isSameOrigin(url) && !init.credentials) {
      init = { ...init, credentials: 'include' };
    }
    const response = await originalFetch(input, init);

    if (response && response.status === 401 && isBackendRequest(input)) {
      const url = typeof input === 'string' ? input : input?.url || '';
      const isAuthEndpoint = url.includes('/api/auth/login')
        || url.includes('/api/auth/register')
        || url.includes('/api/auth/forgot-password')
        || url.includes('/api/auth/reset-password')
        // PIN-/Passwortprüfungen (Tagebuch-Schutz für CRM, Budget, Tagebuch)
        // antworten bei falscher Eingabe legitim mit 401 — das ist KEIN
        // Session-Ablauf und darf den Nutzer nicht ausloggen.
        || url.includes('/api/journal/security/verify')
        || url.includes('/api/journal/security/reset')
        || url.includes('/api/journal/security/force-reset');
      if (!isAuthEndpoint) {
        handleUnauthorized();
      }
    }
    return response;
  };
};

export const serverLogout = async () => {
  // Best-effort cookie clear on the server; failures are non-fatal because
  // local cleanup is what matters for the JWT-based flow.
  try {
    if (BACKEND_URL) {
      const token = localStorage.getItem('token');
      await fetch(`${BACKEND_URL}/api/auth/logout`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      });
    }
  } catch (err) {
    console.warn('[authClient] server logout failed (non-fatal)', err);
  } finally {
    clearLocalAuth();
  }
};

export { clearLocalAuth };
